import { AlignerMonitoringCell, type DataTableColumn } from '@odo/shared-ui';
import type { DmInboxStatus, DmNotificationSort } from '../../../services/dmNotifications';
import { ClosureCell } from '../shared/ClosureCell';
import { INBOX_COLUMN_CHROME, opensUpwards } from '../shared/inboxColumnStyles';
import { withRowExit } from '../shared/inboxColumns';
import { InboxViewLink } from '../shared/InboxViewLink';
import { WaitingCell } from '../shared/WaitingCell';
import { NotificationAssigneeCell } from './NotificationAssigneeCell';
import { NotificationLevelCounts } from './NotificationLevelCounts';
import { NotificationLine } from './NotificationLine';
import { NotificationPatientCell } from './NotificationPatientCell';
import { NotificationReviewAction } from './NotificationReviewAction';
import { notificationViewPath, type FromNotificationListState } from './notificationListParams';
import type { NotificationTableRow } from './notificationRows';

// The patient cell's pl-5 + pr-3 in px; the span trims the same 20 + 12 at its two ends.
const FIRST_CELL_PADDING_PX = 20 + 12;

// Fixed widths: under auto layout every expand/collapse would renegotiate the columns and shift the table.
// Waiting and Closed share a slot; the percentages sum to 100.
const COLUMN_PERCENT = {
    patient: 25,
    dm: 6,
    assignee: 12,
    levels: 27,
    waiting: 12,
    view: 18
} as const;

// The sub-row spans patient..waiting from inside the patient cell, whose content box the % resolves against.
const SPANNED_PERCENT =
    COLUMN_PERCENT.patient +
    COLUMN_PERCENT.dm +
    COLUMN_PERCENT.assignee +
    COLUMN_PERCENT.levels +
    COLUMN_PERCENT.waiting;
const SPAN_RATIO = SPANNED_PERCENT / COLUMN_PERCENT.patient;
const SPAN_EXTRA_PX = (SPAN_RATIO - 1) * FIRST_CELL_PADDING_PX;
const NOTIFICATION_LINE_WIDTH = `calc(${SPAN_RATIO * 100}% + ${SPAN_EXTRA_PX}px)`;

const FROM_LIST_STATE: FromNotificationListState = { fromNotificationList: true };

type NotificationColumn = DataTableColumn<NotificationTableRow, DmNotificationSort>;

function percent(value: number): string {
    return `${value}%`;
}

function patientColumn(onToggle: (patientId: string) => void): NotificationColumn {
    return {
        ...INBOX_COLUMN_CHROME,
        key: 'patient',
        label: 'Patient',
        width: percent(COLUMN_PERCENT.patient),
        render: (row) =>
            row.kind === 'notification' ? (
                <NotificationLine notification={row.notification} width={NOTIFICATION_LINE_WIDTH} />
            ) : (
                <NotificationPatientCell
                    row={row.patient}
                    expanded={row.expanded}
                    onToggle={onToggle}
                />
            )
    };
}

const DM_COLUMN: NotificationColumn = {
    ...INBOX_COLUMN_CHROME,
    key: 'dm',
    label: 'DM',
    width: percent(COLUMN_PERCENT.dm),
    render: (row) =>
        row.kind === 'patient' ? (
            <AlignerMonitoringCell
                current={row.patient.currentAligner}
                total={row.patient.totalAligners}
                monitoringStatus={row.patient.monitoringStatus}
            />
        ) : null
};

function assigneeColumn(rowCount: number): NotificationColumn {
    return {
        ...INBOX_COLUMN_CHROME,
        key: 'assignee',
        label: 'Assignee',
        width: percent(COLUMN_PERCENT.assignee),
        // No overflow-hidden: the dropdown renders in-place and would be clipped.
        stopClickPropagation: true,
        render: (row, index) =>
            row.kind === 'patient' ? (
                <NotificationAssigneeCell
                    row={row.patient}
                    dropUp={opensUpwards(index, rowCount)}
                />
            ) : null
    };
}

const LEVELS_COLUMN: NotificationColumn = {
    ...INBOX_COLUMN_CHROME,
    key: 'levels',
    label: 'By level',
    width: percent(COLUMN_PERCENT.levels),
    sortable: true,
    sortKey: 'level',
    render: (row) =>
        row.kind === 'patient' ? <NotificationLevelCounts counts={row.patient.levelCounts} /> : null
};

const WAITING_COLUMN: NotificationColumn = {
    ...INBOX_COLUMN_CHROME,
    key: 'waiting',
    label: 'Waiting',
    width: percent(COLUMN_PERCENT.waiting),
    sortable: true,
    sortKey: 'waiting',
    render: (row) =>
        row.kind === 'patient' ? (
            <WaitingCell
                oldestAt={row.patient.oldestAt}
                latestAt={row.patient.latestAt}
                inbox="notifications"
            />
        ) : null
};

// Closed rows sort by when they closed, which the API calls latest.
const CLOSED_COLUMN: NotificationColumn = {
    ...INBOX_COLUMN_CHROME,
    key: 'closed',
    label: 'Closed',
    width: percent(COLUMN_PERCENT.waiting),
    sortable: true,
    sortKey: 'latest',
    render: (row) =>
        row.kind === 'patient' ? <ClosureCell resolution={row.patient.lastResolution} /> : null
};

function actionsColumn(
    onReviewed: (notificationId: string) => Promise<void>,
    listSearch: string
): NotificationColumn {
    return {
        ...INBOX_COLUMN_CHROME,
        key: 'view',
        label: 'Actions',
        headerNode: null,
        width: percent(COLUMN_PERCENT.view),
        align: 'right',
        stopClickPropagation: true,
        render: (row) =>
            row.kind === 'notification' ? (
                <NotificationReviewAction notification={row.notification} onReviewed={onReviewed} />
            ) : (
                <InboxViewLink
                    to={notificationViewPath(row.patient.patientId, listSearch)}
                    state={FROM_LIST_STATE}
                    ariaLabel={`View notifications for ${row.patient.patientName}`}
                />
            )
    };
}

interface BuildNotificationColumnsArgs {
    rowCount: number;
    onToggle: (patientId: string) => void;
    onReviewed: (notificationId: string) => Promise<void>;
    listSearch: string;
    status: DmInboxStatus;
}

export function buildNotificationColumns({
    rowCount,
    onToggle,
    onReviewed,
    listSearch,
    status
}: BuildNotificationColumnsArgs): NotificationColumn[] {
    return [
        patientColumn(onToggle),
        assigneeColumn(rowCount),
        DM_COLUMN,
        LEVELS_COLUMN,
        status === 'closed' ? CLOSED_COLUMN : WAITING_COLUMN,
        actionsColumn(onReviewed, listSearch)
    ].map(withRowExit);
}
